import { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { any, object, TypeOf, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { clockoutTimesheet } from "../../redux/actions/timesheet.actions";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { green } from "@mui/material/colors";
import { Box, Button, TextField, FormHelperText } from "@mui/material";
import Mapbox from "../../components/Locationmap/Mapbox";
import MapContext from "../../components/Locationmap/MapContext";
import { TimePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment";

dayjs.extend(customParseFormat);

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: any;
  action: string;
  filterData: any;
}

const formSchema = object({
  id: any(),
  shift_end_date: z.string().min(1, "Shift End Date is required."),
  shift_end_time: z.string().min(1, "Start end time is required."),
  shift_comments: z.string(),
  authorized_wardnamnager: z.string(),
  shift_end_lat_lang: z
    .string()
    .nonempty("Company Lat/Lang coordinats are required")
    .regex(/^(-?\d+(\.\d+)?)(,\s*(-?\d+(\.\d+)?))$/, "Invalid Lag Lang"),
});

type FormInput = TypeOf<typeof formSchema>;

const defaultValues = {
  shift_end_date: "",
  shift_end_time: "",
  shift_comments: "",
  authorized_wardnamnager: "",
};

export function ClockoutForm(props: Props) {
  const { handleCloseDialog } = props;
  const dispatch = useDispatch();
  const { timesheet, save } = useSelector(
    (state: any) => state.employeetimesheet
  );
  const loading = useSelector((state: any) => state.UI.loading);
  const {
    register,
    reset,
    getValues,
    setValue,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInput>({ resolver: zodResolver(formSchema) });
  const [latlng, setLatlng] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [handelaction] = useState("");

  const handleClose = useCallback(() => {
    handleCloseDialog(false);
    reset(defaultValues);
  }, [handleCloseDialog, reset]);

  useEffect(() => {
    if (save) {
      handleClose();
    }
  }, [save, handleClose]);

  useEffect(() => {
    if (timesheet) reset(timesheet);
    if (timesheet?.shift_end_lat_lang) {
      setValue("shift_end_lat_lang", timesheet.shift_end_lat_lang);
    } else if (timesheet?.shift_start_lat_lang)
      setValue("shift_end_lat_lang", timesheet.shift_start_lat_lang);

    //setValue('shift_end_lat_lang"',timesheet?shift_end_lat_lang ? timesheet.shift_end_lat_lang: timesheet.shift_start_lat_lang )
  }, [timesheet, reset, setValue]);

  useEffect(() => {
    if (latlng) {
      reset({ ...getValues(), shift_end_lat_lang: latlng });
    }
  }, [latlng, reset, getValues]);

  const onFormSubmit: SubmitHandler<FormInput> = (data) => {
    const formData = new FormData();
    formData.append("shift_end_date", data.shift_end_date);
    formData.append("shift_end_time", data.shift_end_time);
    formData.append("shift_comments", data.shift_comments);
    formData.append("authorized_wardnamnager", data.authorized_wardnamnager);
    formData.append("id", data.id);
    if (data.id) {
      dispatch<any>(clockoutTimesheet(formData, props.filterData));
    }
  };

  const handleClick = (event: any) => {
    setIsOpen(!isOpen);
  };

  return (
    <MapContext.Provider value={{ latlng, setLatlng }}>
      <Dialog open={props.isDialogOpened} onClose={handleClose} fullWidth>
        <DialogTitle>{props.action}</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            id="companyform"
            name="companyform"
            noValidate
            onSubmit={handleSubmit(onFormSubmit)}
          >
            <TextField
              autoFocus
              id="shift_end_date"
              margin="dense"
              fullWidth
              label="Shift End Date"
              placeholder="--"
              required
              type="date"
              inputProps={{
                max: moment().format("YYYY-MM-DD"),
              }}
              variant="standard"
              defaultValue=""
              {...register("shift_end_date")}
              error={!!errors["shift_end_date"]}
              helperText={
                errors["shift_end_date"] ? errors["shift_end_date"].message : ""
              }
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Controller
              control={control}
              name="shift_end_time"
              render={({ field: { onChange, value } }) => (
                <>
                  <TimePicker
                    placeholder="End Time"
                    onChange={(time, timestring) => {
                      onChange(timestring);
                    }}
                    status={!!errors["shift_end_time"] ? "error" : ""}
                  />
                  <FormHelperText error={!!errors["shift_end_time"]}>
                    {errors["shift_end_time"]
                      ? errors["shift_end_time"].message
                      : ""}
                  </FormHelperText>
                </>
              )}
            />
            <TextField
              margin="dense"
              id="shift_comments"
              label="Comment"
              fullWidth
              variant="standard"
              InputLabelProps={{
                shrink: true,
              }}
              {...register("shift_comments")}
              error={!!errors["shift_comments"]}
              helperText={
                errors["shift_comments"] ? errors["shift_comments"].message : ""
              }
            />

            <TextField
              margin="dense"
              id="authorized_wardnamnager"
              label="Authorized Ward Manager"
              fullWidth
              variant="standard"
              InputLabelProps={{
                shrink: true,
              }}
              {...register("authorized_wardnamnager")}
              error={!!errors["authorized_wardnamnager"]}
              helperText={
                errors["authorized_wardnamnager"]
                  ? errors["authorized_wardnamnager"].message
                  : ""
              }
            />

            <TextField
              margin="dense"
              id="name"
              label="Checkout Lat/Lng"
              fullWidth
              variant="standard"
              required
              {...register("shift_end_lat_lang")}
              error={!!errors["shift_end_lat_lang"]}
              helperText={
                errors["shift_end_lat_lang"]
                  ? errors["shift_end_lat_lang"].message
                  : ""
              }
              onClick={handleClick}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <DialogActions>
              <Box sx={{ m: 1, position: "relative" }}>
                <Button
                  type="submit"
                  form="companyform"
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  Save
                </Button>
                {loading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: green[500],
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Box>
              <Button
                onClick={handleClose}
                variant="contained"
                color="error"
                disabled={loading}
              >
                Cancel
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
      <Mapbox
        isDialogOpened={isOpen}
        handleCloseDialog={() => setIsOpen(false)}
        action={handelaction}
      />
    </MapContext.Provider>
  );
}
